import { Injectable } from '@angular/core';
import { IUser } from 'src/app/shared/interface/user.interface';
import { RESTService } from './rest.service';
import { StorageService } from './storage.service';
import { map, Observable } from 'rxjs';
import { HttpHeaderType } from 'src/app/shared/enum/headerType.enum';
import { IUserStatus } from 'src/app/shared/interface/userStatus.interface';
@Injectable({
  providedIn: 'root'
})

/** This class handles the server communication and application storage for the user objects */
export class UserService {

  constructor(private _storage: StorageService, private _rest: RESTService) { }

  /**
   * This method is used to get all the users
   * @returns {Observable<any>}
   */
  public getAll() {
    return this._rest.get('users');
  }

  /**
   * This method is used to get all the users
   * @returns {Observable<any>}
   */
  public getAllFromCompany(id: number) {
    return this._rest.get('users/company/' + id);
  }

  /**
   * This method is used to get all the users except super admins
   * @returns {Observable<any>}
   */
  public getAllNotSuperadminsFromCompany(id: number) {
    return this._rest.get('users/company/no-admin/' + id);
  }
  /**
   * This method is used to get all the devices from a user
   * @returns {Observable<any>}
   */
  public getAllUserDevices(id: number) {
    return this._rest.get('users/devices/' + id);
  }
  /**
   * This function sets a user object in the storage.
   * @param {IUser} user
   */
  setUser(user: IUser) {
    this._storage.set('user', user);
  }

  /**
   * This function gets a user object from the storage.
   * @returns {IUser}
   */
  getCurrentUser(): IUser {
    return JSON.parse(this._storage.get('user'));
  }

  /**
   * This function retrieves a user by their ID and returns an observable of
   * the user object.
   * @param {number} id - The parameter "id" is a number representing the unique identifier of a user.
   * This method is used to retrieve a user object from the server based on their ID.
   * @returns {Observable<IUser>} An Observable of type IUser is being returned.
   */
  getUserById(id: number): Observable<IUser> {
    return this._rest.get('users/' + id,).pipe(map((result) => {
      let user = result.data;
      return user;
    }));
  }

  /**
   * This function returns an observable that retrieves a user's profile data from a REST API.
   * @returns {Observable<IUser>} An Observable of type IUser is being returned.
   */
  getUserProfile(): Observable<IUser> {
    return this._rest.get('users/profile').pipe(map((result) => {
      let user = result.data;
      return user;
    }));
  }

  /**
   * This function sets a token in the storage.
   * @param {string} token
   */
  setToken(token: string) {
    this._storage.set('token', token);
  }

  /**
   * This function returns a token in the storage.
   * @return {string}
   */
  getToken(): string {
    return this._storage.get('token');
  }


  /**
   * This function updates the status of a user with a given ID.
   * @param {number} userId
   * @param {number} statusId
   * @returns The `setStatus` function is returning the result of a PUT request to the endpoint
   * `users/status`.
   */
  setStatus(userId: number, statusId: number) {
    return this._rest.put('users/status', {id: userId, statusId: statusId});
  }

  /**
   * This function returns a token in the storage.
   * @return {IUserStatus}
   */
  getStatus(): IUserStatus {
    return this.getCurrentUser().status;
  }

  /**
   * This function updates a user using a PUT request to a REST API.
   * @param {IUser} user
   * @returns The `updateUser` function is returning the result of a PUT request to the 'users'
   * endpoint with the updated user object passed as a parameter.
   */
  updateUser(user: IUser) {
    // console.log('user');
    // console.log(user);
    return this._rest.put('users', {user: user});
  }

  /**
   * This function creates a new user by sending a POST request to the 'users' endpoint with the user
   * object as the payload.
   * @param {IUser} user
   * @returns Observable
   */
  createUser(user: IUser) {
    // console.log(user);
    return this._rest.post('users', {user: user});
  }

  /**
   * This function updates a user's profile using a PUT request to a REST API endpoint.
   * @param {IUser} user
   * @returns Observable
   */
  updateUserProfile(user: IUser) {
    return this._rest.put('users/profile', {user: user});
  }

  /**
   * This function updates a user's profile image using a POST request to a REST API endpoint.
   * @param {FormData} formData
   * @returns Observable
   */
  updateUserProfileImage(formData: FormData) {
    return this._rest.post('users/profile/image', formData);
  }

  /**
   * This function deletes a user with a specified ID using a REST API call.
   * @param {number} userId
   * @returns Observable
   */
  delete(userId: number) {
    return this._rest.delete('users/' + userId);
  }

    /**
   * This function deletes a user with a specified ID using a REST API call.
   * @param {number} userId
   * @returns Observable
   */
    deleteDevice(deviceId: number) {
      return this._rest.delete('users/device/' + deviceId);
    }

  /**
   * This function retrieves a user by their ID and returns an observable of
   * the user object.
   * @param {any} userList - The parameter "id" is a number representing the unique identifier of a user.
   * This method is used to retrieve a user object from the server based on their ID.
   * @returns {Observable<any>} An Observable of type IUser is being returned.
   */
  addUserlist(userList: any):Observable<any> {
    return this._rest.post('users/import', {userlist: userList});
  }

}
